import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { importBespokeTemplatesBundleBespokeTemplatesImportBundlePost } from '../../services/api/sdk.gen';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { useToast } from '../../hooks/useToastContext';
import { fetchRogerEmrFormSchemaVersions } from './fetchRogerEmrFormSchemaVersions';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import { bespokeApiErrorMessage } from './formSchemaUtils';
import { type BespokeTemplateExportRow } from './bespokeTemplatesExport';
import {
  bespokeRowsByLeafKey,
  buildImportNestedGroups,
  collectLeafKeysFromGroups,
  exportRowsToImportUpserts,
  importNestedGroupsToSelectableNestedRows,
  parseBespokeImportBundleJson,
  resolveLegacyImportRowPins,
  type ImportFormTypeGroup,
} from './bespokeTemplatesImport';

export function useBespokeTemplatesImport({
  open,
  formTypeCatalog,
  onImported,
  onClose,
}: {
  open: boolean;
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  onImported: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const importFileRef = useRef<HTMLInputElement>(null);
  const importSelectionInitPickGenRef = useRef(-1);
  /** Last `importBundlePickGeneration` we've already warned about for unresolved legacy pins. */
  const importUnresolvedWarnedGenRef = useRef(-1);

  const [importPrepLoading, setImportPrepLoading] = useState(false);
  const [importParseError, setImportParseError] = useState<string | null>(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importBundleSummary, setImportBundleSummary] = useState<{
    filename: string;
    formatVersion: number;
    exportScope?: string;
    exportedAt?: string;
    rowCount: number;
  } | null>(null);
  const [importSourceRows, setImportSourceRows] = useState<
    BespokeTemplateExportRow[]
  >([]);
  const [importVersionsByFormType, setImportVersionsByFormType] = useState<
    Map<RogerDocFormType, FormSchemaVersion[]>
  >(() => new Map());
  const [importNestedGroups, setImportNestedGroups] = useState<
    ImportFormTypeGroup[]
  >([]);
  const [importRowByKey, setImportRowByKey] = useState<
    Map<string, BespokeTemplateExportRow>
  >(() => new Map());
  const [importSelectedKeys, setImportSelectedKeys] = useState<Set<string>>(
    () => new Set()
  );
  /** Increments when a JSON bundle is successfully parsed — initial row selection uses this. */
  const [importBundlePickGeneration, setImportBundlePickGeneration] =
    useState(0);

  useEffect(() => {
    if (!open || formTypeCatalog.length === 0) return;
    let cancelled = false;
    void (async () => {
      setImportPrepLoading(true);
      /** Avoid grouping against a stale slice from a previous dialog session. */
      setImportVersionsByFormType(new Map());
      try {
        const m = new Map<RogerDocFormType, FormSchemaVersion[]>();
        await Promise.all(
          formTypeCatalog.map(async row => {
            const vers = await fetchRogerEmrFormSchemaVersions(
              row.canonical_visit_type_id
            );
            m.set(row.form_type as RogerDocFormType, vers);
          })
        );
        if (!cancelled) {
          setImportVersionsByFormType(m);
        }
      } catch (err) {
        console.error('Import modal: failed to load schema versions:', err);
        if (!cancelled) {
          toastRef.current.error(
            'Could not load schema versions',
            'Grouping may be incomplete until you reopen the import dialog.'
          );
          setImportVersionsByFormType(new Map());
        }
      } finally {
        if (!cancelled) {
          setImportPrepLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, formTypeCatalog]);

  /** When the import dialog opens or closes, reset its client state (avoids stale filename / trees). */
  useEffect(() => {
    if (!open) {
      setImportPrepLoading(false);
      setImportVersionsByFormType(new Map());
    }
    setImportParseError(null);
    setImportBundleSummary(null);
    setImportSourceRows([]);
    setImportNestedGroups([]);
    setImportRowByKey(new Map());
    setImportSelectedKeys(new Set());
    setImportSubmitting(false);
    setImportBundlePickGeneration(0);
    importSelectionInitPickGenRef.current = -1;
    importUnresolvedWarnedGenRef.current = -1;
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (importSourceRows.length === 0) {
      importSelectionInitPickGenRef.current = -1;
      setImportNestedGroups([]);
      setImportRowByKey(new Map());
      setImportSelectedKeys(new Set());
      return;
    }
    if (importPrepLoading || formTypeCatalog.length === 0) {
      setImportNestedGroups([]);
      setImportRowByKey(new Map());
      setImportSelectedKeys(new Set());
      return;
    }

    // Legacy bundles (format_version <= 4) don't carry form_type/form_schema_version
    // per row — infer them from the catalog. Ambiguous/unknown names are dropped and
    // reported so the user can fix the bundle instead of importing a guessed pin.
    const { rows: resolvedRows, unresolved } = resolveLegacyImportRowPins({
      rows: importSourceRows,
      catalog: formTypeCatalog,
      versionsByFormType: importVersionsByFormType,
    });
    if (
      unresolved.length > 0 &&
      importUnresolvedWarnedGenRef.current !== importBundlePickGeneration
    ) {
      importUnresolvedWarnedGenRef.current = importBundlePickGeneration;
      const names = [...new Set(unresolved.map(r => r.schema_version_name))];
      toastRef.current.error(
        'Some rows could not be matched to a form type',
        `${unresolved.length} row(s) skipped (schema version name unknown or shared by multiple form types): ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` (+${names.length - 3} more)` : ''}. Re-export from a current environment to include a form_type on each row.`
      );
    }

    const groups = buildImportNestedGroups({
      templates: resolvedRows,
      catalog: formTypeCatalog,
    });
    setImportNestedGroups(groups);
    const allKeys = collectLeafKeysFromGroups(groups);
    setImportRowByKey(bespokeRowsByLeafKey(resolvedRows));

    const isNewParsedBundle =
      importSelectionInitPickGenRef.current !== importBundlePickGeneration;
    if (isNewParsedBundle) {
      importSelectionInitPickGenRef.current = importBundlePickGeneration;
    }

    setImportSelectedKeys(prev => {
      if (isNewParsedBundle) {
        return new Set(allKeys);
      }
      const next = new Set<string>();
      for (const k of allKeys) {
        if (prev.has(k)) next.add(k);
      }
      return next;
    });
  }, [
    formTypeCatalog,
    importBundlePickGeneration,
    open,
    importPrepLoading,
    importSourceRows,
    importVersionsByFormType,
  ]);

  const importAllLeafKeys = useMemo(
    () => collectLeafKeysFromGroups(importNestedGroups),
    [importNestedGroups]
  );

  const importSelectableRows = useMemo(
    () =>
      importNestedGroupsToSelectableNestedRows(
        importNestedGroups,
        formatSectionDropdownLabel
      ),
    [importNestedGroups]
  );

  const handleImportBundleFileChosen = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) return;
      setImportParseError(null);

      try {
        const text = await file.text();
        const json: unknown = JSON.parse(text);
        const parsed = parseBespokeImportBundleJson(json);

        const filtered = parsed.templates;

        if (filtered.length === 0) {
          setImportParseError('The bundle contains no layouts.');
          setImportBundleSummary(null);
          setImportSourceRows([]);
          return;
        }

        setImportBundleSummary({
          filename: file.name,
          formatVersion: parsed.formatVersion,
          exportScope: parsed.exportScope,
          exportedAt: parsed.exportedAt,
          rowCount: filtered.length,
        });
        setImportSourceRows(filtered);
        setImportBundlePickGeneration(g => g + 1);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Could not read import file.';
        setImportParseError(msg);
        setImportBundleSummary(null);
        setImportSourceRows([]);
      } finally {
        input.value = '';
      }
    },
    []
  );

  const handleRunBespokeImport = useCallback(async () => {
    const rows = [...importSelectedKeys]
      .map(k => importRowByKey.get(k))
      .filter((r): r is BespokeTemplateExportRow => r != null);

    if (rows.length === 0) {
      toast.error('Nothing to import', 'Select at least one section.');
      return;
    }

    setImportSubmitting(true);
    try {
      const res =
        await importBespokeTemplatesBundleBespokeTemplatesImportBundlePost({
          throwOnError: true,
          body: {
            templates: exportRowsToImportUpserts(rows),
          },
        });
      const created = res.data?.created_count ?? 0;
      const updated = res.data?.updated_count ?? 0;

      toast.success(
        'Import complete',
        `${created} created, ${updated} updated (always upserts).`
      );
      onImported();
      onClose();
    } catch (err) {
      console.error('Bespoke template import failed:', err);
      toast.error('Import failed', bespokeApiErrorMessage(err));
    } finally {
      setImportSubmitting(false);
    }
  }, [importRowByKey, importSelectedKeys, onClose, onImported, toast]);

  return {
    importFileRef,
    importPrepLoading,
    importParseError,
    importSubmitting,
    importBundleSummary,
    importSourceRows,
    importNestedGroups,
    importSelectedKeys,
    setImportSelectedKeys,
    importAllLeafKeys,
    importSelectableRows,
    handleImportBundleFileChosen,
    handleRunBespokeImport,
  };
}
